import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FORM_URL = "https://readdy.ai/api/form/db8h49jk13ra8mig8rmg";
const CONTACT_EMAIL = "support@waiwaimanhua.example";

const values = [
  { icon: "ri-book-open-line", title: "丰富资源", desc: "涵盖热血、校园、恋爱、科幻、悬疑等多元题材，持续扩充内容。" },
  { icon: "ri-search-eye-line", title: "便捷检索", desc: "支持关键词搜索与类型、地区、连载状态多条件组合筛选。" },
  { icon: "ri-smartphone-line", title: "多端阅读", desc: "手机与电脑自适应，阅读页支持滑动浏览与缩放调节。" },
  { icon: "ri-heart-3-line", title: "本地收藏", desc: "收藏状态保存在浏览器本地，无需登录即可标记心仪作品。" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [toast, setToast] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useDocumentMeta({
    title: "关于我们 - 歪歪漫画在线观看",
    description:
      "了解歪歪漫画在线观看的平台定位、服务内容与联系方式，查看免责声明与版权说明。",
    keywords: "关于歪歪漫画,歪歪漫画在线观看,漫画平台介绍,联系方式",
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      showToast("邮箱已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动记录邮箱");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    formData.delete("website_alt");
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setSubmitting(true);
    setFormError("");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.code || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请稍后重试。"
            : `提交失败：${serverMsg || "服务暂时不可用，请稍后重试。"}`
        );
      }
    } catch (error) {
      setStatus("error");
      setFormError(
        error instanceof Error
          ? `提交失败：${error.message}`
          : "提交失败：网络异常，请稍后重试。"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar />
      <main>
        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto w-full max-w-6xl px-4 md:px-8 py-14">
            <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">关于歪歪漫画在线观看</h1>
            <p className="mt-4 max-w-3xl text-sm text-foreground-600 leading-relaxed md:text-base">
              歪歪漫画在线观看是一个专注于高清漫画在线阅读的平台，面向广大漫画爱好者，尤其服务于青少年与年轻成年人群体。
              我们希望用丰富的内容与流畅的体验，让每一位读者都能轻松发现并沉浸在自己喜欢的漫画世界里。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 md:px-8 py-12">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${v.icon} text-2xl`}></i>
                </span>
                <h2 className="mt-4 font-heading text-lg text-foreground-900">{v.title}</h2>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 md:px-8 pb-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl text-foreground-950">联系我们</h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                如果你对平台有任何建议、内容反馈或合作意向，欢迎通过以下方式与我们取得联系。
              </p>

              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className="ri-mail-line"></i>
                    </span>
                    <span className="text-sm text-foreground-700">{CONTACT_EMAIL}</span>
                  </div>
                  <button
                    type="button"
                    onClick={copyEmail}
                    className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-600 hover:border-primary-400 hover:text-primary-600 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-file-copy-line"></i>
                    复制
                  </button>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-time-line"></i>
                  </span>
                  <span className="text-sm text-foreground-700">服务时间：每日 09:00 - 22:00</span>
                </div>
              </div>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base text-foreground-900">免责声明与版权</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  本站所有漫画作品名称、封面、简介及章节图片均为虚构演示内容，仅用于产品功能展示，不涉及任何真实作品、真实人物、评分或奖项。
                  站内搜索、筛选、收藏与阅读功能均为前端演示，不提供真实内容的播放或下载服务，相关接口待接入真实服务。
                </p>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  © 2026 歪歪漫画在线观看. 保留所有权利。
                </p>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                <h2 className="font-heading text-2xl text-foreground-950">意见反馈</h2>
                <p className="mt-2 text-sm text-foreground-500">
                  填写下面的表单，把你的想法告诉我们（仅为前端演示提交，将发送至平台表单服务）。
                </p>

                {status === "success" && (
                  <div className="mt-5 flex items-center gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700">
                    <i className="ri-checkbox-circle-line"></i>
                    提交成功，感谢你的反馈！
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-5 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
                    <i className="ri-error-warning-line"></i>
                    {formError || "提交失败，请稍后重试。"}
                  </div>
                )}

                <form data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-5" id="about-feedback-form">
                  <div>
                    <label htmlFor="fb-name" className="mb-1.5 block text-sm text-foreground-700">你的称呼</label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="fb-email" className="mb-1.5 block text-sm text-foreground-700">邮箱</label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复的邮箱"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="fb-message" className="mb-1.5 block text-sm text-foreground-700">反馈内容</label>
                    <textarea
                      id="fb-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述你的建议或遇到的问题（不超过 500 字）"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="form-guard-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                    {submitting ? "提交中…" : "提交反馈"}
                  </button>
                </form>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading text-lg text-foreground-900">友情提示</h3>
                <ul className="mt-3 space-y-2 text-sm text-foreground-600">
                  <li className="flex items-start gap-2"><i className="ri-check-line mt-0.5 text-primary-500"></i> 想找特定漫画？试试顶部的搜索功能。</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line mt-0.5 text-primary-500"></i> 想按题材浏览？前往分类页体验多条件筛选。</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line mt-0.5 text-primary-500"></i> 喜欢的作品可以点击收藏，方便下次快速找到。</li>
                </ul>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/search" className="inline-flex items-center gap-2 rounded-md bg-secondary-500 px-4 py-2 text-sm text-background-50 hover:bg-secondary-600 whitespace-nowrap">
                    <i className="ri-search-line"></i> 去搜索
                  </Link>
                  <Link to="/category/全部" className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 whitespace-nowrap">
                    <i className="ri-apps-2-line"></i> 浏览分类
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-900 px-5 py-2.5 text-sm text-background-50 animate-fade-up">
          {toast}
        </div>
      )}
      <Footer />
    </div>
  );
}